/**
 * Data-picker helpers (ADR-0068 F4, revised for the Schema-lens picker).
 *
 * The Input pane's SCHEMA LENS is the binding picker (n8n model): each field
 * row is draggable into an ƒx field and clickable to insert. These helpers are
 * the pure pieces it builds bindings from:
 *
 *   - the current node's input  → base `$json`
 *   - an upstream node "Label"  → base `$('Label').item.json`
 *
 * The old `buildBindingTree`/`PickerNode` tree (a separate "Campi" tab
 * rendered by the deleted `DataPickerTree`) is GONE — the Schema lens shows
 * the same shape with presence/type info, so a second tree was a worse copy.
 */

/** The MIME type a Schema-lens drag carries (alongside `text/plain`) so drop
 *  targets can tell a binding drag from an arbitrary text/file drag. */
export const BINDING_DRAG_MIME = "application/x-tray-binding";

/** A safe JS identifier gets dot access (`.name`); anything else needs
 *  bracket access (`["weird-key"]`) so the emitted expression is valid. */
export function accessFor(key: string): string {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key) ? `.${key}` : `[${JSON.stringify(key)}]`;
}

/** Extract the representative item data from a node I/O payload — the first
 *  item's `.json` for a serialised `list[Item]` batch, else the first array
 *  element, else the value itself. Mirrors `ItemBatchViewer.normalise()`'s
 *  `rows[0]`, so the picker binds the SAME shape the viewer shows. */
export function representativeData(payload: unknown): unknown {
  if (Array.isArray(payload) && payload.length > 0) {
    const first = payload[0];
    if (
      first &&
      typeof first === "object" &&
      !Array.isArray(first) &&
      ("json" in first || "binary" in first || "meta" in first)
    ) {
      return (first as { json?: unknown }).json ?? null;
    }
    return first;
  }
  return payload;
}

/** The `$('Label').item.json` base for an upstream node whose label is `label`. */
export function upstreamBase(label: string): string {
  return `$(${JSON.stringify(label)}).item.json`;
}
